import { useMemo } from 'react'

import { usePrincipal } from '../../../../application/authentication'
import { isInputConfig } from '../../../../application/formBuilder/config'
import { useProposal } from '../../../../application/product/proposal/ProposalProvider'
import { Data, Proposal } from '../../../../domain'
import { useAdjustments } from '../../../../state'
import { FormConfig } from '../../../../state/configuration'
import { useApiErrorHandlingAsyncCallback } from '../../../auth'
import { hasChanges } from '../helpers'
import { useProposalAdjustmentRequest } from './adjustments'
import { useProposalData } from './data'

export const useProposalFormData = (form: FormConfig) => {
  const { proposal } = useProposal()

  const { getAdjustment } = useAdjustments()

  const toData = useProposalData(proposal)

  return useMemo(() => {
    return form.filter(isInputConfig).reduce((data, input) => {
      const adjustment = getAdjustment(input.name)

      return {
        ...data,
        [input.name]: toData(adjustment),
      }
    }, {})
  }, [form, proposal, toData, getAdjustment])
}

interface ProposalAdjustmentProps {
  form: FormConfig
  applyRating: boolean
  onComplete?: (proposal?: Proposal) => Promise<void> | void
}

export const useProposalAdjustment = ({
  form,
  applyRating,
  onComplete,
}: ProposalAdjustmentProps) => {
  const { apiClient } = usePrincipal()
  const { proposal, setProposal } = useProposal()

  const existingData = useProposalFormData(form)
  const proposalAdjustmentRequest = useProposalAdjustmentRequest(form, applyRating)

  return useApiErrorHandlingAsyncCallback(async (data: Data) => {
    const adjustment = proposalAdjustmentRequest(existingData, data)

    const updatedProposal = hasChanges(adjustment)
      ? await apiClient.updateProposal(proposal.proposalCode, adjustment)
      : proposal

    setProposal(updatedProposal)
    await onComplete?.(updatedProposal)
  })
}
